import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import type { ReferralReportRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { SkeletonLines } from '../../../components/Skeleton';
import { STAGE_TONES } from '../../../components/status-tones';
import { EmptyState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDate, formatNumber } from '../../../lib/format';
import { useCanView } from '../../../stores/session';
import { useReferralReport } from '../api/pools-api';
import styles from '../Pools.module.css';
import { useStageLabels } from '../../../lib/modules';

/**
 * F14.09's report (Q5, master plan S25: "referrer, candidates, outcomes"), below the referral
 * links. Recruiters and the HR Lead only — everyone else sees their own links and nothing more.
 */
export function ReferralReport(): ReactNode {
  const canView = useCanView('talent_pool');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openReferrer, setOpenReferrer] = useState<string | null>(null);
  const report = useReferralReport(from || undefined, to || undefined, canView);

  if (!canView) return null;

  return (
    <Card title="Referral report" meta="Who referred whom, and how it ended. Hired counts anyone who accepted or joined.">
      <div className={styles.matchBar}>
        <Field label="Referred from">
          {({ inputId }) => <DatePicker id={inputId} label="Referred from" value={from} onChange={setFrom} />}
        </Field>
        <Field label="Referred to">
          {({ inputId }) => <DatePicker id={inputId} label="Referred to" value={to} onChange={setTo} />}
        </Field>
      </div>
      {report.error && !report.data ? (
        <ServerErrorState onRetry={() => void report.refetch()} />
      ) : !report.data ? (
        <SkeletonLines count={4} />
      ) : report.data.length === 0 ? (
        <EmptyState isInset title="No referrals in this period" body="Referrals are counted when someone applies through a colleague's link above." action={null} />
      ) : (
        <ul className={styles.list}>
          {report.data.map((row) => (
            <ReferrerRow key={row.referrerUserId ?? row.referrerName} row={row} isOpen={openReferrer === (row.referrerUserId ?? row.referrerName)} onToggle={() => setOpenReferrer((current) => (current === (row.referrerUserId ?? row.referrerName) ? null : (row.referrerUserId ?? row.referrerName)))} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function ReferrerRow({ row, isOpen, onToggle }: { readonly row: ReferralReportRow; readonly isOpen: boolean; readonly onToggle: () => void }): ReactNode {
  const stageLabels = useStageLabels();
  return (
    <li className={styles.referrer}>
      <div className={styles.referrerHead}>
        <span className={styles.name}>{row.referrerName}</span>
        <span className={styles.meta}>
          {formatNumber(row.referred)} referred · {formatNumber(row.hired)} hired · {formatNumber(row.inProcess)} in process · {formatNumber(row.notSelected)} not selected · {formatNumber(row.withdrawn)} withdrew
        </span>
        <Button variant="ghost" size="sm" aria-expanded={isOpen} onClick={onToggle}>
          {isOpen ? 'Hide people' : 'Show people'}
        </Button>
      </div>
      {isOpen && (
        <ul className={styles.people}>
          {row.candidates.map((person) => (
            <li key={person.applicationId} className={styles.person}>
              <Link to="/applications/$applicationId" params={{ applicationId: person.applicationId }} className={styles.name}>
                {person.candidate.fullName}
              </Link>
              <span className={styles.meta}>
                {person.jobTitle} · {formatDate(person.referredAt)}
              </span>
              <Chip tone={STAGE_TONES[person.stage]} plain>
                {stageLabels[person.stage]}
              </Chip>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
